import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  SendOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Error as showError, Success } from "../../../commons/Messages/Messages";
import SignatureField from "../safety/SignatureField";
import { formatDateTime, formatDay } from "../safety/format";
import {
  commentReport,
  getReport,
  prefillReport,
  reportPdfUrl,
  returnReport,
  saveReport,
  signReport,
  submitReport
} from "./api";
import { PLACES, addDays, formatMinutes, parseHours, summarizeWeek } from "./calc";
import { REPORT_STATUS_COLORS, placeLabels, reportStatusLabels, weekdayLabels } from "./labels";
import "../safety/safety.css";

const WITH_ENTRIES = ["BETRIEB", "SCHULE", "UEBERBETRIEBLICH"];

/** What is sent to the server: no client-only fields. */
const payloadOf = week => ({
  days: week.days.map(d => ({
    date: d.date,
    place: d.place,
    activities: d.activities.map(a => ({ text: a.text, minutes: a.minutes }))
  })),
  instructions: { text: week.instructions?.text || "", minutes: week.instructions?.minutes || 0 },
  schoolTopics: week.schoolTopics || ""
});

const HoursInput = ({ minutes, disabled, onChange, ariaLabel }) => {
  const [text, setText] = useState(minutes ? formatMinutes(minutes) : "");
  useEffect(() => {
    if ((parseHours(text) || 0) !== (minutes || 0)) setText(minutes ? formatMinutes(minutes) : "");
  }, [minutes]);
  const invalid = text !== "" && parseHours(text) === null;
  return (
    <Input
      className="ap-hours"
      value={text}
      disabled={disabled}
      inputMode="decimal"
      placeholder="0:00"
      aria-label={ariaLabel}
      status={invalid ? "error" : undefined}
      suffix="h"
      onChange={event => {
        setText(event.target.value);
        const parsed = parseHours(event.target.value);
        if (parsed !== null || event.target.value === "") onChange(parsed || 0);
      }}
      onBlur={() => !invalid && setText(minutes ? formatMinutes(minutes) : "")}
    />
  );
};

/** One Berichtsheft week: the apprentice writes and hands it in, the trainer returns or signs. */
const ReportEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { id } = useParams();
  const history = useHistory();
  const pad = useRef(null);
  const [week, setWeek] = useState(null);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [hints, setHints] = useState({});
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState("");
  const places = placeLabels(t);
  const statuses = reportStatusLabels(t);
  const weekdays = weekdayLabels(t);

  const show = data => {
    setWeek(data);
    setSaved(JSON.stringify(payloadOf(data)));
  };

  useEffect(() => {
    getReport(id)
      .then(res => show(res.data.data))
      .catch(generalErrorAlert);
  }, [id]);

  if (!week) {
    return (
      <div className="apprenticeship-page sf-center">
        <Spin />
      </div>
    );
  }

  const editable = week.can.edit;
  const dirty = JSON.stringify(payloadOf(week)) !== saved;
  const summary = summarizeWeek(week);
  const setDay = (index, patch) =>
    setWeek(prev => ({
      ...prev,
      days: prev.days.map((d, i) => (i === index ? { ...d, ...patch } : d))
    }));
  const setActivity = (dayIndex, index, patch) =>
    setDay(dayIndex, {
      activities: week.days[dayIndex].activities.map((a, i) =>
        i === index ? { ...a, ...patch } : a
      )
    });

  const run = async (action, message) => {
    setBusy(true);
    try {
      const res = await action();
      if (res?.data?.data) show(res.data.data);
      if (message) Success(message);
      return res;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => saveReport(week._id, payloadOf(week)), t("Saved"));

  const prefill = async () => {
    const res = await run(() => prefillReport(week._id));
    if (!res) return;
    const proposal = res.data.days;
    setHints(Object.fromEntries(proposal.map(d => [d.date, d])));
    setWeek(prev => ({
      ...prev,
      days: prev.days.map((day, i) => {
        const proposed = proposal[i];
        // Only days the apprentice has not written yet are filled
        if (!proposed || day.activities.some(a => a.text || a.minutes)) return day;
        return { ...day, place: proposed.place, activities: proposed.activities };
      })
    }));
    Success(
      res.data.workedMinutes
        ? t("Working times: {{hours}} h. Describe what you did on each day.", {
            hours: res.data.worked
          })
        : t("No working times recorded for this week.")
    );
  };

  const confirmDialog = async () => {
    if (dialog === "return") {
      if (comment.trim().length < 3) return;
      const res = await run(
        () => returnReport(week._id, comment.trim()),
        t("Returned to the apprentice")
      );
      if (res) setDialog(null);
      return;
    }
    if (!pad.current || pad.current.isEmpty()) {
      showError(t("Please sign in the box."));
      return;
    }
    const image = pad.current.toDataURL();
    if (dialog === "submit") {
      if (dirty && !(await run(() => saveReport(week._id, payloadOf(week))))) return;
      const res = await run(() => submitReport(week._id, { image }), t("Handed in"));
      if (res) setDialog(null);
    } else {
      const res = await run(() => signReport(week._id, { image }), t("Signed"));
      if (res) setDialog(null);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    const res = await run(() => commentReport(week._id, comment.trim()));
    if (res) setComment("");
  };

  const warningText = w =>
    ({
      NOTHING: t("Nothing written yet."),
      EMPTY_DAY: t("{{day}}: no entry", { day: formatDay(w.date) }),
      LONG_DAY: t("{{day}}: more than 10 hours", { day: formatDay(w.date) })
    })[w.code];

  return (
    <div className="apprenticeship-page">
      <div className="sf-header mb-3">
        <Button
          icon={<ArrowLeftOutlined />}
          aria-label={t("Back")}
          onClick={() => history.goBack()}
        />
        <div className="sf-header-title">
          <Typography.Title level={4}>
            {week.label} · {formatDay(week.weekStart)} – {formatDay(addDays(week.weekStart, 6))}
          </Typography.Title>
          <Tag color={REPORT_STATUS_COLORS[week.status]}>{statuses[week.status]}</Tag>
          <Tag>{t("Year {{n}}", { n: week.trainingYear })}</Tag>
        </div>
        <Space wrap>
          <Button icon={<FilePdfOutlined />} href={reportPdfUrl(week._id)} target="_blank">
            PDF
          </Button>
          {editable && (
            <Button icon={<ClockCircleOutlined />} loading={busy} onClick={prefill}>
              {t("Fill from working times")}
            </Button>
          )}
          {editable && (
            <Button icon={<SaveOutlined />} disabled={!dirty} loading={busy} onClick={save}>
              {t("Save")}
            </Button>
          )}
          {week.can.submit && (
            <Button type="primary" icon={<SendOutlined />} onClick={() => setDialog("submit")}>
              {t("Hand in")}
            </Button>
          )}
          {week.can.review && (
            <>
              <Button icon={<RollbackOutlined />} onClick={() => setDialog("return")}>
                {t("Return")}
              </Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => setDialog("sign")}>
                {t("Sign")}
              </Button>
            </>
          )}
        </Space>
      </div>

      {week.status === "returned" && week.relation === "self" && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t(
            "Your trainer returned this week. Please see the comments, change it and hand it in again."
          )}
        />
      )}
      {week.status === "signed" && (
        <Alert
          className="mb-3"
          type="success"
          showIcon
          message={t("Signed by {{name}} on {{date}}", {
            name: week.trainerSignature?.name,
            date: formatDateTime(week.trainerSignature?.signedAt, i18n.language)
          })}
        />
      )}

      <Row gutter={[12, 12]} className="mb-3">
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Company")} value={formatMinutes(summary.workMinutes)} suffix="h" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={t("Vocational school")}
              value={formatMinutes(summary.schoolMinutes)}
              suffix="h"
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={t("Instructions")}
              value={formatMinutes(summary.instructionsMinutes)}
              suffix="h"
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Total")} value={formatMinutes(summary.totalMinutes)} suffix="h" />
          </Card>
        </Col>
      </Row>

      {editable && summary.warnings.length > 0 && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={summary.warnings.map(warningText).join(" · ")}
        />
      )}

      <Row gutter={[16, 0]}>
        <Col xs={24} lg={15}>
          {week.days.map((day, dayIndex) => {
            const hint = hints[day.date];
            const minutes = summary.perDay[dayIndex]?.minutes || 0;
            const showEntries = WITH_ENTRIES.includes(day.place) || day.activities.length > 0;
            return (
              <div className="ap-day" key={day.date}>
                <div className="ap-day-head">
                  <span className="ap-day-title">
                    {weekdays[dayIndex]} {formatDay(day.date)}
                  </span>
                  {hint?.hint && <Tag>{hint.hint}</Tag>}
                  {hint?.workedMinutes > 0 && (
                    <Tag icon={<ClockCircleOutlined />}>{formatMinutes(hint.workedMinutes)} h</Tag>
                  )}
                  <Select
                    size="small"
                    value={day.place}
                    disabled={!editable}
                    onChange={value => setDay(dayIndex, { place: value })}
                    options={PLACES.map(p => ({ value: p, label: places[p] }))}
                    popupMatchSelectWidth={false}
                  />
                  <span className="ap-total">{minutes ? `${formatMinutes(minutes)} h` : ""}</span>
                </div>
                {showEntries &&
                  day.activities.map((activity, index) => (
                    <div className="ap-activity" key={index}>
                      <Input.TextArea
                        className="ap-activity-text"
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        value={activity.text}
                        disabled={!editable}
                        maxLength={2000}
                        placeholder={
                          day.place === "SCHULE" ? t("Lesson topics") : t("What did you do?")
                        }
                        onChange={event =>
                          setActivity(dayIndex, index, { text: event.target.value })
                        }
                      />
                      <HoursInput
                        minutes={activity.minutes}
                        disabled={!editable}
                        ariaLabel={t("Hours")}
                        onChange={value => setActivity(dayIndex, index, { minutes: value })}
                      />
                      {editable && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={t("Delete")}
                          onClick={() =>
                            setDay(dayIndex, {
                              activities: day.activities.filter((_, i) => i !== index)
                            })
                          }
                        />
                      )}
                    </div>
                  ))}
                {editable && showEntries && (
                  <Button
                    size="small"
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setDay(dayIndex, {
                        activities: [...day.activities, { text: "", minutes: 0 }]
                      })
                    }
                  >
                    {t("Add entry")}
                  </Button>
                )}
              </div>
            );
          })}
        </Col>
        <Col xs={24} lg={9}>
          <Card
            size="small"
            title={t("Instructions, in-company lessons, other training")}
            className="mb-3"
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 10 }}
              value={week.instructions?.text || ""}
              disabled={!editable}
              maxLength={5000}
              onChange={event =>
                setWeek(prev => ({
                  ...prev,
                  instructions: { ...prev.instructions, text: event.target.value }
                }))
              }
            />
            <Space className="mt-2">
              <span className="sf-small">{t("Hours")}</span>
              <HoursInput
                minutes={week.instructions?.minutes || 0}
                disabled={!editable}
                ariaLabel={t("Hours")}
                onChange={value =>
                  setWeek(prev => ({
                    ...prev,
                    instructions: { ...prev.instructions, minutes: value }
                  }))
                }
              />
            </Space>
          </Card>
          <Card size="small" title={t("Topics of the vocational school")} className="mb-3">
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 10 }}
              value={week.schoolTopics || ""}
              disabled={!editable}
              maxLength={5000}
              onChange={event => setWeek(prev => ({ ...prev, schoolTopics: event.target.value }))}
            />
          </Card>
          <Card size="small" title={t("Comments")}>
            <List
              dataSource={week.comments || []}
              locale={{ emptyText: t("No comments") }}
              renderItem={c => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <span>{c.byName}</span>
                        <span className="sf-small">{formatDateTime(c.at, i18n.language)}</span>
                      </Space>
                    }
                    description={<div className="ap-comment">{c.text}</div>}
                  />
                </List.Item>
              )}
            />
            {week.can.comment && week.status !== "signed" && (
              <Space.Compact className="sf-full mt-2">
                <Input
                  value={comment}
                  maxLength={2000}
                  placeholder={t("Write a comment")}
                  onChange={event => setComment(event.target.value)}
                  onPressEnter={addComment}
                />
                <Button onClick={addComment} disabled={!comment.trim()} loading={busy}>
                  {t("Send")}
                </Button>
              </Space.Compact>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        rootClassName="apprenticeship-page"
        open={Boolean(dialog)}
        width={640}
        title={
          dialog === "submit"
            ? t("Hand in the week")
            : dialog === "return"
              ? t("Return to the apprentice")
              : t("Sign the week")
        }
        okText={dialog === "return" ? t("Return") : dialog === "submit" ? t("Hand in") : t("Sign")}
        cancelText={t("Cancel")}
        okButtonProps={{ disabled: dialog === "return" && comment.trim().length < 3 }}
        confirmLoading={busy}
        onOk={confirmDialog}
        onCancel={() => setDialog(null)}
        destroyOnClose
        maskClosable={false}
      >
        {dialog === "return" ? (
          <Input.TextArea
            autoSize={{ minRows: 3 }}
            value={comment}
            maxLength={2000}
            placeholder={t("What should be changed?")}
            onChange={event => setComment(event.target.value)}
          />
        ) : (
          <>
            <Typography.Paragraph type="secondary">
              {dialog === "submit"
                ? t(
                    "With your signature you confirm that the entries are correct. The week can then only be changed if your trainer returns it."
                  )
                : t(
                    "With your signature you confirm the record of this week. It is then locked and archived as PDF."
                  )}
            </Typography.Paragraph>
            <SignatureField
              ref={pad}
              label={
                dialog === "submit"
                  ? t("Signature of the apprentice")
                  : t("Signature of the trainer")
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReportEditor;
